import PropTypes from "prop-types";
import { useState } from "react";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// API's
import { getCommissionCycles } from "@/Common/api/commissionCycles";
import { getUserPayPlanAssignmentPage } from "@/PayPlans/api/users/queries";

// Utils
import { getPositionDepartment } from "@/Common/helpers/Positions";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Layouts
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmployeeMeta from "@/Layouts/components/EmployeeMeta";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Tables
import PayPlanHistory from "@PayPlans/components/Tables/PayPlanHistory/PayPlanHistory";
import PayPlanSection from "@/PayPlans/components/Tables/PayPlanSection";

// MUI
import { Divider, Grid, Skeleton, Typography } from "@mui/material";
import { Box } from "@mui/system";

export default function Individual(props) {
	const { selectedItem = null, locationId = "" } = props;

	const queryClient = useQueryClient();
	const [showRowFeedback, setShowRowFeedback] = useState(false);

	/*********************************************** SUBSCRIPTIONS ***********************************************/
	const { getSubscription } = useSubscriptions();
	const payPlanAssignmentSubscription = getSubscription(["payPlanAssignment", selectedItem?._id]);
	payPlanAssignmentSubscription?.setInvalidate(["payPlanAssignmentUserInformation", selectedItem._id]);

	payPlanAssignmentSubscription?.subscriptions?.onCreatePayPlanAssignment?.setCallback((result) => {
		const userInformation = result.data?.onCreatePayPlanAssignment || null;
		const auxArray = [];
		userInformation?.users[0]?.communications?.forEach((item) => {
			if (item.communicationType === "EMAIL") {
				userInformation.users[0]["email"] = item.value;
			}
			if (item.communicationType === "PHONE") {
				userInformation.users[0]["phone"] = item.value;
			}
		});
		userInformation?.users[0]?.payPlanAssignmentHistory?.forEach((element) => {
			auxArray.push({
				id: element._id,
				_id: element._id,
				planId: element.planId.name,
				assignmentDate: element.assignmentDate,
				assignedBy: element.assignedBy,
				effectiveDate: element.effectiveDate,
				acknowledgementStatus: element.acknowledgementStatus,
				acknowledgementDocumentLink: element?.acknowledgementDocumentLink,
				position: element?.position,
				auditEvidenceLink: element?.auditEvidenceLink,
				locationId: userInformation?.users[0]?.locationId,
			});
		});
		userInformation.users[0]["payPlanAssignmentHistoryUpdated"] = auxArray;

		const currentAcknowledgementStatus = userInformation?.users?.[0]?.currentPayPlanAssignment?.acknowledgementStatus || null;
		const upcomingAcknowledgementStatus = userInformation?.users?.[0]?.upcomingPayPlanAssignment?.acknowledgementStatus || null;

		if (userInformation?.users?.[0]?.currentPayPlanAssignment) {
			userInformation.users[0].currentPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				currentAcknowledgementStatus?.[currentAcknowledgementStatus?.length - 1];
		}

		if (userInformation?.users?.[0]?.upcomingPayPlanAssignment) {
			userInformation.users[0].upcomingPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				upcomingAcknowledgementStatus?.[upcomingAcknowledgementStatus?.length - 1];
		}

		queryClient.setQueryData(["payPlanAssignmentUserInformation", userInformation.users[0]._id], userInformation.users[0]);
	});

	payPlanAssignmentSubscription?.subscriptions?.onRemoveUserPayPlanAssignment?.setCallback((result) => {
		const auxArray = [];
		const userInformation = result.data?.onRemoveUserPayPlanAssignment || null;

		userInformation?.communications?.forEach((item) => {
			if (item.communicationType === "EMAIL") {
				userInformation["email"] = item.value;
			}
			if (item.communicationType === "PHONE") {
				userInformation["phone"] = item.value;
			}
		});
		userInformation.payPlanAssignmentHistory?.forEach((element) => {
			auxArray.push({
				id: element._id,
				_id: element._id,
				planId: element.planId.name,
				assignmentDate: element.assignmentDate,
				assignedBy: element.assignedBy,
				effectiveDate: element.effectiveDate,
				acknowledgementStatus: element.acknowledgementStatus,
				acknowledgementDocumentLink: element?.acknowledgementDocumentLink,
				position: element?.position,
				auditEvidenceLink: element?.auditEvidenceLink,
				locationId: userInformation?.locationId,
			});
		});
		userInformation["payPlanAssignmentHistoryUpdated"] = auxArray;

		const currentAcknowledgementStatus = userInformation?.currentPayPlanAssignment?.acknowledgementStatus || null;
		const upcomingAcknowledgementStatus = userInformation?.upcomingPayPlanAssignment?.acknowledgementStatus || null;

		if (userInformation?.currentPayPlanAssignment) {
			userInformation.currentPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				currentAcknowledgementStatus?.[currentAcknowledgementStatus?.length - 1];
		}

		if (userInformation?.upcomingPayPlanAssignment) {
			userInformation.upcomingPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				upcomingAcknowledgementStatus?.[upcomingAcknowledgementStatus?.length - 1];
		}
		queryClient.setQueryData(["payPlanAssignmentUserInformation", userInformation._id], userInformation);
	});

	// SUBSCRIPTION FOR ACKNOWLEDGEMENT STATUS UPDATE
	payPlanAssignmentSubscription?.subscriptions?.onUpdateUserPayPlanAssignment?.setCallback((result) => {
		const auxArray = [];
		const userInformation = result.data?.onUpdateUserPayPlanAssignment || null;

		userInformation?.communications?.forEach((item) => {
			if (item.communicationType === "EMAIL") {
				userInformation["email"] = item.value;
			}
			if (item.communicationType === "PHONE") {
				userInformation["phone"] = item.value;
			}
		});

		userInformation?.payPlanAssignmentHistory?.forEach((element) => {
			auxArray.push({
				id: element._id,
				_id: element._id,
				planId: element.planId.name,
				assignmentDate: element.assignmentDate,
				assignedBy: element.assignedBy,
				effectiveDate: element.effectiveDate,
				acknowledgementStatus: element.acknowledgementStatus,
				acknowledgementDocumentLink: element?.acknowledgementDocumentLink,
				position: element?.position,
				auditEvidenceLink: element?.auditEvidenceLink,
				locationId: userInformation?.locationId,
			});
		});

		userInformation["payPlanAssignmentHistoryUpdated"] = auxArray;
		const currentAcknowledgementStatus = userInformation?.currentPayPlanAssignment?.acknowledgementStatus || null;
		const upcomingAcknowledgementStatus = userInformation?.upcomingPayPlanAssignment?.acknowledgementStatus || null;

		if (userInformation?.currentPayPlanAssignment) {
			userInformation.currentPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				currentAcknowledgementStatus?.[currentAcknowledgementStatus?.length - 1];
		}

		if (userInformation?.upcomingPayPlanAssignment) {
			userInformation.upcomingPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
				upcomingAcknowledgementStatus?.[upcomingAcknowledgementStatus?.length - 1];
		}

		queryClient.setQueryData(["payPlanAssignmentUserInformation", userInformation._id], userInformation);
		setShowRowFeedback(false);
	});

	/*********************************************** QUERIES ***********************************************/
	const { data: currentCommissionCycle } = useQuery({
		queryKey: ["currentPayPlanAssignmentCommissionCycle", locationId],
		queryFn: async () => {
			const allCommissionCycles = await getCommissionCycles(locationId);
			return allCommissionCycles[allCommissionCycles.length - 1];
		},
		enabled: !!locationId,
		staleTime: Infinity,
	});

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ["payPlanAssignmentUserInformation", selectedItem?._id],
		queryFn: async () => {
			let emailRef = "";
			let phoneRef = "";

			const userPayPlanAssignment = await getUserPayPlanAssignmentPage(selectedItem?._id, locationId);

			const auxArray = userPayPlanAssignment?.payPlanAssignmentHistory
				?.filter((element) => element?.locationId === locationId) // Filter elements by current locationId
				?.map((element) => ({
					id: element?._id,
					_id: element?._id,
					planId: element?.planId?.name,
					assignmentDate: element?.assignmentDate,
					assignedBy: element?.assignedBy,
					effectiveDate: element?.effectiveDate,
					acknowledgementStatus: element?.acknowledgementStatus,
					acknowledgementDocumentLink: element?.acknowledgementDocumentLink,
					position: element?.position,
					auditEvidenceLink: element?.auditEvidenceLink,
					locationId: element?.locationId,
				}));

			userPayPlanAssignment?.communications?.forEach((item) => {
				if (item.communicationType === "EMAIL") {
					emailRef = item.value;
				}
				if (item.communicationType === "PHONE") {
					phoneRef = item.value;
				}
			});
			userPayPlanAssignment["email"] = emailRef;
			userPayPlanAssignment["phone"] = phoneRef;
			userPayPlanAssignment["payPlanAssignmentHistoryUpdated"] = auxArray;

			const currentAcknowledgementStatus = userPayPlanAssignment?.currentPayPlanAssignment?.acknowledgementStatus;
			const upcomingAcknowledgementStatus = userPayPlanAssignment?.upcomingPayPlanAssignment?.acknowledgementStatus;
			if (userPayPlanAssignment?.currentPayPlanAssignment) {
				userPayPlanAssignment.currentPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
					currentAcknowledgementStatus?.[currentAcknowledgementStatus?.length - 1];
			}

			if (userPayPlanAssignment?.upcomingPayPlanAssignment) {
				userPayPlanAssignment.upcomingPayPlanAssignment["latestPayPlanAcknowledgementStatus"] =
					upcomingAcknowledgementStatus?.[upcomingAcknowledgementStatus?.length - 1];
			}

			return userPayPlanAssignment;
		},
		staleTime: Infinity,
		enabled: !!selectedItem?._id && !!locationId,
	});

	const department = getPositionDepartment(data?.position);

	return (
		<RoundedBox sx={{ minHeight: "630px" }} className="!p-[16px] !pb-[48px]">
			<Grid container className="!px-[16px]">
				<EmployeeMeta
					data={{ ...data, reportsTo: data?.supervisor?.fullName, department: `${department} Department` }}
					loading={isLoading}
					columns={[
						["POSITION", "DEPARTMENT", "REPORTS_TO"],
						["ID", "EMAIL", "PHONE"],
					]}
				/>
			</Grid>
			<Grid container width={1} className="!px-[16px]">
				<Divider className="!border-disabled" variant="fullWidth" sx={{ width: "100%" }} />
			</Grid>

			<Grid container>
				<Grid item xs={12}>
					<PayPlanSection
						data={data || null}
						locationId={props?.locationId}
						loading={Boolean(isLoading || isFetching)}
						commissionCycle={currentCommissionCycle}
						showRowFeedback={showRowFeedback}
						setShowRowFeedback={setShowRowFeedback}
					/>
				</Grid>
			</Grid>

			<Grid container className="!px-[16px]">
				<Grid item xs={12}>
					{isLoading || isFetching ? (
						<>
							<Box pt={3.5}>
								<Skeleton
									sx={{
										borderRadius: "6px",
									}}
									animation="wave"
									variant="rectangular"
									width={280}
									height={30}
								/>
							</Box>
							<Box
								mb={2}
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									columnGap: 2,
								}}
							>
								<Skeleton variant="circular" width={30} height={30} />
								<Skeleton variant="circular" width={30} height={30} />
							</Box>

							<TableLoader mt={1} content rows={3} multiTitles={5} />
						</>
					) : (
						<Grid container direction="row" justifyContent="space-between">
							<Grid item xs={6} sx={{ paddingTop: "48px", marginBottom: "16px" }}>
								<Typography className="page-title" component="div">
									{"Assignment History"}
								</Typography>
							</Grid>
							<Grid item xs={12} sx={{ width: "100%" }}>
								<PayPlanHistory
									loading={Boolean(isLoading || isFetching)}
									data={data?.payPlanAssignmentHistoryUpdated || []}
								/>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Grid>
		</RoundedBox>
	);
}
Individual.propTypes = {
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
	}),
	locationId: PropTypes.string,
};
